import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import AuthAccountRecoveryModal from './AuthAccountRecoveryModal';
import SignUpModal from './SignUpModal';
import GoogleMarkIcon from './GoogleMarkIcon';
import { signInWithEmail, signInWithGoogle } from '../../services/authService';
import { isLocalStorageQuotaError } from '../../utils/localStorageQuota';
import BrandLogo from '../BrandLogo';

const REMEMBER_EMAIL_KEY = 'curriculumplanner-login-email';

function normalizeLoginError(raw: string, err?: unknown): string {
  const msg = (raw || '').trim();
  if (isLocalStorageQuotaError(err) || isLocalStorageQuotaError(msg) || /exceeded the quota/i.test(msg)) {
    return '브라우저 저장 공간이 가득 차 로그인 정보를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.';
  }
  if (!msg) return '로그인에 실패했습니다.';
  if (msg.includes('Email not confirmed')) {
    return '이메일 인증이 필요합니다. 메일함을 확인해 주세요.';
  }
  if (
    /failed to fetch|networkerror|network error|fetch failed/i.test(msg) ||
    /self signed certificate/i.test(msg) ||
    /\b522\b/.test(msg)
  ) {
    return (
      '인증 서버에 연결하지 못했습니다. 네트워크/VPN/보안 프로그램 설정을 확인하고 다시 시도해 주세요. ' +
      '계속 실패하면 Supabase 프로젝트 상태(일시 중지·장애)도 확인해 주세요.'
    );
  }
  return msg;
}

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  onLogin: () => void | Promise<void>;
}

const LoginModal: React.FC<LoginModalProps> = ({ open, onClose, onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(true);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [signUpOpen, setSignUpOpen] = useState(false);

  useEffect(() => {
    if (open) {
      try {
        const saved = window.localStorage.getItem(REMEMBER_EMAIL_KEY);
        if (saved) setEmail(saved);
      } catch {
        /* ignore */
      }
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('이메일과 비밀번호를 입력해주세요.');
      return;
    }
    setIsLoading(true);
    try {
      await signInWithEmail(email, password);
      if (rememberEmail) {
        try {
          window.localStorage.setItem(REMEMBER_EMAIL_KEY, email.trim());
        } catch {
          /* ignore */
        }
      } else {
        try {
          window.localStorage.removeItem(REMEMBER_EMAIL_KEY);
        } catch {
          /* ignore */
        }
      }
      await onLogin();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      setError(normalizeLoginError(msg, err));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 신규(자동 회원가입)·기존 사용자 구분 없이 바로 Google 로그인으로 이동한다.
   * 약관 동의는 로그인 완료 후 App 레벨 게이트(동의 기록이 없을 때만 1회) 에서 확인한다.
   */
  const handleGoogleSignIn = async () => {
    setError('');
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
      // Google 로그인은 리다이렉트되므로 여기서 onClose를 호출하지 않음
    } catch (err: unknown) {
      setError(
        isLocalStorageQuotaError(err)
          ? '브라우저 저장 공간이 가득 차 로그인 정보를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.'
          : err instanceof Error
            ? err.message
            : 'Google 로그인에 실패했습니다.'
      );
      setIsGoogleLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      <div className="cp-modal-backdrop cp-auth-modal-backdrop" onClick={handleBackdropClick}>
        <div className="cp-modal cp-login-modal" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="cp-login-modal-close"
            onClick={onClose}
            aria-label="닫기"
          >
            ×
          </button>

          <div className="login-card">
            <h1 className="login-title">
              <BrandLogo className="cp-brand-logo--login" />
            </h1>
            <p className="login-subtitle">수업 계획·교육과정을 한곳에서 관리합니다</p>

            <form className="login-form" onSubmit={handleEmailLogin}>
              <div className="login-input-group">
                <label className="login-label" htmlFor="modal-login-email">
                  이메일
                </label>
                <input
                  id="modal-login-email"
                  type="email"
                  className="login-input"
                  placeholder="example@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                />
              </div>

              <div className="login-input-group">
                <label className="login-label" htmlFor="modal-login-password">
                  비밀번호
                </label>
                <input
                  id="modal-login-password"
                  type="password"
                  className="login-input"
                  placeholder="비밀번호 입력"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>

              <div className="login-options-row">
                <label className="login-option-label">
                  <input
                    type="checkbox"
                    className="login-option-checkbox"
                    checked={rememberEmail}
                    onChange={(e) => setRememberEmail(e.target.checked)}
                  />
                  아이디(이메일) 기억하기
                </label>
              </div>

              <button type="submit" className="login-btn" disabled={isLoading}>
                {isLoading ? '로그인 중...' : '로그인'}
              </button>
            </form>

            <div className="login-divider">
              <span>또는</span>
            </div>

            <button
              type="button"
              className="login-google-btn"
              onClick={() => void handleGoogleSignIn()}
              disabled={isGoogleLoading}
            >
              {isGoogleLoading ? (
                '로그인 중...'
              ) : (
                <>
                  <GoogleMarkIcon />
                  Google로 로그인
                </>
              )}
            </button>

            <div className="login-footer-links">
              <button
                type="button"
                className="login-footer-link"
                onClick={() => setRecoveryOpen(true)}
              >
                계정/비밀번호 찾기
              </button>
              <span className="login-footer-sep" aria-hidden>
                |
              </span>
              <button
                type="button"
                className="login-footer-link"
                onClick={() => setSignUpOpen(true)}
              >
                회원가입
              </button>
            </div>

            {error && <p className="login-error">{error}</p>}
          </div>
        </div>
      </div>

      <AuthAccountRecoveryModal
        open={recoveryOpen}
        onClose={() => setRecoveryOpen(false)}
      />
      <SignUpModal
        open={signUpOpen}
        onClose={() => setSignUpOpen(false)}
        onSignedIn={() => {
          setSignUpOpen(false);
          void onLogin();
          onClose();
        }}
      />
    </>,
    document.body
  );
};

export default LoginModal;
